import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, Inject } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { switchMap } from 'rxjs/operators';

import { TargetingResultModel } from '../../../../Discover/Targeting/models/targeting-result.model';
import { TargetingSearchService } from '../../../../Discover/Targeting/services/targeting-search.service';
import { ContactService } from '../../../TargetingShared/services/contact-service';
import { DiscoverRoutingService } from '../../../../Discover/DiscoverRouter/services/discover-routing.service';
import { TargetingListService } from '../../../../Discover/Targeting/services/targeting-list.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ListPickerService } from '../../services/list-picker.service';
import { ContactsPublicApi } from '../../../../api/targeting/contacts-public.api';
import { ResultCardDirective } from '../../../TargetingShared/components/ResultCard/result-card.directive';
import { OutletContactTabType, TargetingRhpContextsService } from '../../../TargetingShared/services/targeting-rhp-contexts.service';
import { EntityTypeDefaultImageEnum } from '../../../Misc/enums/entity-type-default-image.enum';
import { EmploymentFactory } from '../../../../Discover/Targeting/factories/employment.factory';
import { promiseOnlyResolved } from '../../../../Core/utils';
import { ManageListDialogComponent } from '../../../../Discover/Targeting/components/TargetingResults/ManageListDialog/manage-list-dialog.component';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { TargetingRhpDialogService } from '../../../TargetingShared/services/targeting-rhp-dialog.service';
import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { DistributionPickerService } from '../../services/distribution-picker.service';
import { ManageDistDialogComponent } from '../../../../Discover/Targeting/components/TargetingResults/ManageDistDialog/manage-dist-dialog.component';
import { DataConnectionService } from '../../../../Core/Users/services/data-connection.service';
import { EmailConversationService } from '../../../../Discover/Targeting/services/email-conversation.service';

export enum CardMode {
    searchResults,
    editorMatchedContact
}

export interface EmploymentChange {
    contact: TargetingResultModel;
    activeNoticeChange: boolean;
}


@Component({
    selector: 'agpr-contact-card',
    templateUrl: './contact-card.component.html',
    styleUrls: ['../../../TargetingShared/components/ResultCard/result-card.scss'],
    animations: [
        trigger('showMoreContent', [
            state('collapsed', style({
                height: '0',
            })),
            state('expanded', style({
                height: '*',
            })),
            transition('expanded <=> collapsed', animate('400ms cubic-bezier(.43,.43,.65,.99)'))
        ])
    ],
    standalone: false
})
export class ContactCardComponent extends ResultCardDirective implements OnInit, OnDestroy {

    @Input() cardMode: CardMode = CardMode.searchResults;
    @Input() contextMenuTemplate: TemplateRef<any> = null;
    @Input() initiallyFocusable: boolean = true;

    @Output() showContactEditor: EventEmitter<void> = new EventEmitter<void>();
    @Output() showRhpTakeOver?: EventEmitter<TargetingResultModel> = new EventEmitter<TargetingResultModel>();
    @Output() focus: EventEmitter<TargetingResultModel> = new EventEmitter<TargetingResultModel>();
    @Output() expand: EventEmitter<TargetingResultModel> = new EventEmitter<TargetingResultModel>();
    @Output() activeNoticeChanged: EventEmitter<void> = new EventEmitter<void>();
    @Output() employmentContextChanged: EventEmitter<EmploymentChange> = new EventEmitter<EmploymentChange>();

    readonly cardModes = CardMode;
    readonly messageSeverity = messageSeverityEnum;

    selectedContactId: string;
    imageDefault = EntityTypeDefaultImageEnum;
    animationState: string = 'collapsed';
    reachTooltip: string;

    constructor(
        public targetingSearchService: TargetingSearchService,
        public targetingListService: TargetingListService,
        public listPickerService: ListPickerService,
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        public contactService: ContactService,
        public discoverRoutingService: DiscoverRoutingService,
        public contactsPublicApi: ContactsPublicApi,
        public targetingRhpContextsService: TargetingRhpContextsService,
        protected employmentFactory: EmploymentFactory,
        public targetingRhpDialogService: TargetingRhpDialogService,
        public distributionPickerService: DistributionPickerService,
        @Inject (APP_BASE_HREF) public appBaseHref: string,
        public dataConnectionService: DataConnectionService,
        public emailConversationService: EmailConversationService
    ) {
        super(targetingSearchService,
            targetingListService,
            listPickerService,
            localizationService,
            coreDialogService,
            targetingRhpContextsService,
            distributionPickerService);
        this.subscriptions.push(
            this.targetingRhpContextsService.getContactId().subscribe(contactId => {
                this.selectedContactId = contactId;
            }),
            this.targetingSearchService.rhpSelectedEmployment().subscribe(res => {
                if (res) {
                    this.rhpSelectedEmploymentChanged();
                }
            })
        );
    }

    ngOnInit(): void {
        if (this.entityInternal.hasActiveNotice) {
            this.toggleExpansion();
        }
        this.reachTooltip = this.isReachValueFromSimilarWeb ? this.localizationService.transform(
            'Common.audienceReach.similarweb.tooltip.insightsBy',
            {src: this.appBaseHref + 'assets/images/SimilarWeb-logo-with-text.svg'}
        ) : null;
    }

    editContact(item: TargetingResultModel) {
        this.contactService.setEditingContact(item.id);
        // TODO : Ideally we should update the URL but not lose the "context"
        // so that the user can share the page but still be able to close the overlay and return
        // to his original context
        this.showContactEditor.emit();
    }

    deleteContact(item: TargetingResultModel) {
        this.contactService.deleteContacts([item]);
    }

    focusOutlet(event: any) {
        event.preventDefault();
        if (this.cardMode === CardMode.searchResults) {
            this.targetingRhpContextsService.setEntityByResult(this.entityInternal);
            this.targetingRhpContextsService.selectOutletContactTab(OutletContactTabType.outletProfile);
        } else {
            this.targetingRhpDialogService.openTakeOver(
                {
                    inputs: {
                        contactId: this.entityInternal.id,
                        outletId: this.entityInternal.selectedEmployment.outlet.id,
                        entityType : EntityType.OUTLET,
                        isPrivate: false
                    }
                }
            );
        }

    }

    focusContact(event: any){
        event?.preventDefault(event);
        this.targetingRhpContextsService.setEntityByResult(this.entityInternal);
        this.targetingRhpContextsService.selectOutletContactTab(OutletContactTabType.contactProfile);
    }

    selectedEmploymentChanged() {
        const hasActiveNotice = this.entityInternal.hasActiveNotice;
        if (hasActiveNotice) {
            this.entityInternal.hasActiveNotice = false;
            this.toggleExpansion();
        }
        this.employmentContextChanged.emit({
            contact: this.entityInternal,
            activeNoticeChange: hasActiveNotice
        });
        if (this.selectedContactId === this.entityInternal.id) {
            this.targetingRhpContextsService.changeSelectedEmployment(this.entityInternal.selectedEmployment.id);
        }
        this.setDisplayedReachValue();
    }

    toggleExpansion() {
        if (this.expanded) {
            this.expanded = false;
            this.animationState = 'collapsed';
        } else {
            if (!this.entityInternal.loadedEmployments) {
                this.contactsPublicApi.getContact(this.entityInternal.id).pipe(
                    switchMap(contact => {
                        const employmentPromises = contact.employments.map(x => this.employmentFactory.create(x));
                        return promiseOnlyResolved(employmentPromises);
                    }),
                ).subscribe(employments => {
                    this.entityInternal.loadedEmployments = employments;
                    // Update the ref so the radio button behaves
                    if (!this.entityInternal.hasActiveNotice) {
                        this.entityInternal.selectedEmployment = this.entityInternal.loadedEmployments
                            .find(x => x.id === this.entityInternal.selectedEmployment.id);
                    }
                    this.expanded = true;
                    this.animationState = 'expanded';
                });
            } else {
                this.expanded = true;
                this.animationState = 'expanded';
            }
        }
    }

    rhpSelectedEmploymentChanged(): void {
        if (this.expanded) {
            this.entityInternal.selectedEmployment = this.entityInternal.loadedEmployments
                .find(x => x.id === this.entityInternal.selectedEmployment.id);
        }
    }

    cardFocused() {
        this.focus.emit(this.entityInternal);
    }

    expandClicked() {
        this.expand.emit(this.entityInternal);
    }

    openManageListDialog(entity: TargetingResultModel) {
        this.coreDialogService.open(ManageListDialogComponent, {
            inputs: {
                setEntity: entity
            },
            outputs: {}
        });
    }

    openManageDistDialog(entity: TargetingResultModel) {
        this.coreDialogService.open(ManageDistDialogComponent, {
            inputs: {
                setEntity: entity
            },
            outputs: {}
        });
    }

    composeEmail(): void {
        this.cardFocused();
        this.emailConversationService.composeEmail(this.entityInternal.id);
    }
}
